/**
 * Keeps Zapier's OAuth parameters across a QuoteMate login.
 *
 * A user arriving without a session is sent through Kinde by `UserRequiredContainer`. Only one
 * of its branches carries the query string: the email-first path passes
 * `pathname + search` as `returnTo`, while a returning user with a cached provider goes straight
 * to `login()` and comes back on the SDK's fixed redirect URI. Zapier's `state` and PKCE
 * challenge cannot be reconstructed, so they are stashed before the redirect and read back after
 * it, whichever branch ran.
 *
 * sessionStorage rather than Redux: the round trip reloads the app, and rather than
 * localStorage, so a stashed request cannot outlive the tab it belongs to.
 */

import { parseAuthorizeParams } from './zapierAuthorizeParams'

const STORAGE_KEY = 'quotemate_zapier_authorize_request'

/**
 * Matches the pending-authorization expiry on the service. A stash older than this belongs to an
 * attempt that has already failed, and replaying it would send the user into a handshake Zapier
 * is no longer waiting on.
 */
const MAX_AGE_MS = 10 * 60 * 1000

/**
 * saveAuthorizeRequest - Stashes the parameters Zapier sent, to be read back after login.
 *
 * @param {Object<string, string>} params - The parameters from the authorize URL.
 * @returns {void}
 */
export const saveAuthorizeRequest = (params) => {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ params, savedAtMs: Date.now() }))
    } catch (error) {
        // A browser refusing session storage costs this user the login round trip, not the
        // whole flow: arriving with a session still works.
        console.warn('[ZapierAuthorize] Could not stash the authorization request:', error)
    }
}

/**
 * readAuthorizeRequest - Reads back a stashed request, if one survives.
 *
 * @returns {Object<string, string>|null} The stashed parameters, or null when nothing is stored,
 *   the entry is unreadable, or it is older than the pending-authorization expiry.
 */
export const readAuthorizeRequest = () => {
    try {
        const stored = sessionStorage.getItem(STORAGE_KEY)

        if (!stored) {
            return null
        }

        const { params, savedAtMs } = JSON.parse(stored)

        if (!params || typeof savedAtMs !== 'number' || Date.now() - savedAtMs > MAX_AGE_MS) {
            clearAuthorizeRequest()
            return null
        }

        return params
    } catch (error) {
        console.warn('[ZapierAuthorize] Could not read the stashed authorization request:', error)
        return null
    }
}

/**
 * clearAuthorizeRequest - Drops the stash so a later visit cannot replay a spent request.
 *
 * @returns {void}
 */
export const clearAuthorizeRequest = () => {
    try {
        sessionStorage.removeItem(STORAGE_KEY)
    } catch (error) {
        console.warn('[ZapierAuthorize] Could not clear the stashed authorization request:', error)
    }
}

/**
 * zapierAuthorizeLoader - Route loader for the Zapier authorize page.
 *
 * Stashes a complete request before anything renders. The page sits behind
 * `UserRequiredContainer`, which returns only a loading screen while it works out whether the
 * user is authenticated — the page's own component never mounts on that first pass, so nothing
 * else can write the stash in time for a login round trip to read it back.
 *
 * Only saves when the request is complete: this loader also runs on the *return* navigation to
 * this route once authenticated, and by then the query string is Kinde's (`code`, `state`),
 * not Zapier's — saving it would overwrite the very stash the page is about to read back.
 *
 * @param {{ request: Request }} args - React Router loader arguments.
 * @returns {null}
 */
export const zapierAuthorizeLoader = ({ request }) => {
    const { missing, params } = parseAuthorizeParams(new URL(request.url).searchParams)

    if (missing.length === 0) {
        saveAuthorizeRequest(params)
    }

    return null
}
